/**
 * dna-city.js — DENANDROPOLIS. An open-world walk through a monochrome
 * night city, somewhere between deploys.
 *
 * Walk a long boulevard (← → / A D, tap-hold on touch). Silhouette
 * citizens wander and talk to themselves about life and about the
 * architect. Seven buildings can be entered — each one is a piece of
 * the portfolio (projects, the lab, the signal tower, the arcade, the
 * post office…). Generative ambient music: slow pads, gamelan-ish
 * pentatonic plucks, a deep drone, all through a soft reverb.
 *
 * Pure canvas, no dependencies, desktop + mobile.
 */
(() => {
  const host = document.querySelector('[data-city]');
  if (!host) return;

  const canvas = host.querySelector('[data-city-canvas]');
  const ctx = canvas.getContext('2d');
  const titleEl = host.querySelector('[data-city-title]');
  const cardEl = host.querySelector('[data-city-card]');
  const cardKind = host.querySelector('[data-city-card-kind]');
  const cardTitle = host.querySelector('[data-city-card-title]');
  const cardBody = host.querySelector('[data-city-card-body]');
  const cardActions = host.querySelector('[data-city-card-actions]');
  const promptEl = host.querySelector('[data-city-prompt]');
  const ambientBtn = host.querySelector('[data-city-ambient]');
  const hintEl = host.querySelector('[data-city-hint]');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const hash = (n) => {
    const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  };

  /* ================================================================
     Canvas
     ================================================================ */
  let W = 0;
  let H = 0;
  let ground = 0;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const resize = () => {
    const rect = host.getBoundingClientRect();
    W = Math.max(Math.floor(rect.width), 300);
    H = Math.max(Math.floor(rect.height), 380);
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ground = Math.round(H * 0.82);
  };
  resize();
  window.addEventListener('resize', resize);

  /* ================================================================
     World data
     ================================================================ */
  const WORLD = 7200;
  const IG_URL = 'https://www.instagram.com/jdenandro';

  const EXHIBITS = [
    {
      x: 560,
      w: 280,
      h: 190,
      name: 'TERMINAL CAFÉ',
      sign: 'CAFÉ /dev/espresso',
      kind: 'EXHIBIT 01 — PROCESS',
      title: 'How the architect works',
      body:
        'Order a coffee, watch the pipeline. Denandro pairs with Claude Code daily: plan, build, review, ship — a seven-stage loop that turns ideas into deployed features before the cup cools. The café wifi password is, reportedly, "lgtm".',
      actions: [{ label: 'SEE THE PIPELINE', href: '/#claude-flow' }],
    },
    {
      x: 1520,
      w: 300,
      h: 210,
      name: 'THE ARCHIVE',
      sign: 'ARCHIVE — dossiers 01–08',
      kind: 'EXHIBIT 02 — WORK',
      title: 'Eight project dossiers',
      body:
        'Shelved in ink: an AI order-report pipeline, a multi-store sync engine, agentic dashboards, generative storefront experiments. Each dossier is a system that shipped — not a mockup. The librarian is strict about that distinction.',
      actions: [{ label: 'OPEN THE DOSSIERS', href: '/#projects' }],
    },
    {
      x: 2480,
      w: 280,
      h: 200,
      name: 'THE LAB',
      sign: 'LAB — uptime 99.98%',
      kind: 'EXHIBIT 03 — AUTOMATION',
      title: 'Where the agents live',
      body:
        'Behind this door: a console wired to real workflows. Type /report and a day of spreadsheet work happens in seconds. Type /audit and the storefront confesses everything. The machines hum politely. They like it here.',
      actions: [{ label: 'RUN THE CONSOLE', href: '/#lab' }],
    },
    {
      x: 3460,
      w: 240,
      h: 300,
      name: 'SIGNAL TOWER',
      sign: 'SIGNAL — 12K receivers',
      kind: 'EXHIBIT 04 — THE SIGNAL',
      title: '@jdenandro — broadcasting',
      body:
        'The tallest antenna in the district — and it does not transmit code. Twelve thousand souls tune in for the human behind the systems: face-to-camera talks about life, love, and the kind of motivation that survives a Monday. Transmission is free. Following is one tap.',
      actions: [{ label: 'FOLLOW THE SIGNAL ↗', href: IG_URL, external: true }],
    },
    {
      x: 4420,
      w: 280,
      h: 190,
      name: 'INKPIX ARCADE',
      sign: 'ARCADE — 1 credit',
      kind: 'EXHIBIT 05 — PLAY',
      title: 'The cabinet is on',
      body:
        'One machine, one game: DENANDRO.EXE. Outrun the backlog, jump the bugs, bank the commits. The high score board only remembers you. No refunds on dignity.',
      actions: [{ label: 'INSERT COIN', href: '/pages/play' }],
    },
    {
      x: 5380,
      w: 280,
      h: 190,
      name: 'POST OFFICE',
      sign: 'POST — replies < 24h',
      kind: 'EXHIBIT 06 — CONTACT',
      title: 'Send a letter to the studio',
      body:
        'The clerk stamps everything URGENT because, frankly, it usually is. Project briefs, automation ideas, collaboration requests — all delivered to a desk in Bali, GMT+8, where they are read with coffee and answered fast.',
      actions: [{ label: 'WRITE TO DENANDRO', href: '/pages/contact' }],
    },
    {
      x: 6340,
      w: 280,
      h: 230,
      name: 'OBSERVATORY',
      sign: 'OBSERVATORY — skills wing',
      kind: 'EXHIBIT 07 — MASTERY',
      title: 'The constellation of skills',
      body:
        'Through the big telescope: Shopify systems, Liquid, JS, AI agent design, n8n pipelines, analytics — each star measured, none decorative. The astronomer insists the brightest one is "shipping". Hard to argue at this altitude.',
      actions: [{ label: 'VIEW THE STAR MAP', href: '/#mastery' }],
    },
  ];

  const BILLBOARDS = [
    { x: 1050, text: 'DENANDRO — AI AUTOMATION ENGINEER' },
    { x: 2050, text: 'SHIPS DAILY · BALI · GMT+8' },
    { x: 3060, text: '@JDENANDRO — 12K & COUNTING', flicker: true },
    { x: 4980, text: 'ZERO DOWNTIME SINCE TUESDAY' },
    { x: 5900, text: 'VACANCY: YOUR PROJECT HERE', flicker: true },
  ];

  /* ---- npc dialogue ---- */
  const LINES_LIFE = [
    'the fog rolls in at 60fps tonight.',
    'i miss color sometimes. then i remember the aesthetic.',
    'somewhere a deploy is failing. not mine. peace.',
    'rent is cheap in monochrome.',
    'the moon is one big pixel if you squint.',
    'i walk to think. mostly i think about walking.',
    'my houseplant is a vector. never dies.',
    'they paved the alley with border-radius: 0.',
    'quiet night. even the bugs are asleep.',
    'i used to chase trends. now i chase the last train.',
    'grayscale is just honesty with good lighting.',
    'every window is someone shipping something.',
    'the stars? deterministic. comforting, honestly.',
  ];
  const LINES_LORE = [
    'the architect? denandro. builds agents that work while he sleeps.',
    'heard he wired this whole district in black and white. bold.',
    '12k tune in when he talks — life, love, the whole thing. @jdenandro.',
    'the lab uptown syncs a real store. i saw the lights blink.',
    'they say he ships before the coffee cools.',
    'the arcade cabinet runs his code. the runner never stops.',
    'he answers letters within a day. the post office is proud of it.',
    'three years keeping a $6M storefront sharp. that is the résumé.',
    'his camera talks hit harder than his commits. and his commits ship.',
    'the signal tower never sleeps. new talks weekly, they say.',
  ];
  const LINES_GREET = [
    'evening, traveler.',
    'you are not from this district.',
    'the signal tower is worth the walk →',
    'try the café. the pipeline is showing tonight.',
    'mind the drones. kidding — that is the other game.',
    'walk far enough and the city tells you everything.',
  ];
  const LINES_CAT = ['mrrp.', '…', '(judges you, gently)', 'prrr.'];

  const NPCS = [];
  (() => {
    for (let i = 0; i < 14; i++) {
      const seed = i * 7.3 + 2.1;
      const zone = 300 + (i / 14) * (WORLD - 700);
      const walker = hash(seed) > 0.32;
      NPCS.push({
        kind: 'person',
        walker,
        x: zone + hash(seed + 1) * 260,
        x0: zone - 140,
        x1: zone + 320,
        dir: hash(seed + 2) > 0.5 ? 1 : -1,
        speed: 26 + hash(seed + 3) * 34,
        h: 46 + hash(seed + 4) * 14,
        wBody: 12 + hash(seed + 5) * 6,
        white: hash(seed + 6) > 0.62,
        hat: Math.floor(hash(seed + 7) * 3), // 0 none, 1 brim, 2 beanie
        umbrella: hash(seed + 8) > 0.72,
        skirt: hash(seed + 9) > 0.6,
        phase: hash(seed + 10) * 10,
        bubble: null,
        nextOwnTalk: 4000 + hash(seed + 11) * 9000,
        greeted: -99999, // "never" — 0 would suppress greetings for the first 26s
      });
    }
    NPCS.push({
      kind: 'cat',
      walker: false,
      x: 1980,
      dir: -1,
      h: 14,
      phase: 0,
      bubble: null,
      nextOwnTalk: 9000,
      greeted: -99999,
    });
    NPCS.push({
      kind: 'cat',
      walker: false,
      x: 5660,
      dir: 1,
      h: 14,
      phase: 3,
      bubble: null,
      nextOwnTalk: 14000,
      greeted: -99999,
    });
  })();

  /* ================================================================
     Ambient music — generative, mystical, chill
     ================================================================ */
  const AMB_KEY = 'dna_city_amb';
  let ambientOn = true;
  try {
    ambientOn = localStorage.getItem(AMB_KEY) !== '0';
  } catch (error) {
    /* default on */
  }

  const music = (() => {
    let ac = null;
    let master = null;
    let wet = null;
    let running = false;
    let chordTimer = null;
    let pluckTimer = null;
    let chordIndex = 0;

    const CHORDS = [
      [110.0, 164.81, 220.0, 261.63, 329.63], // Am
      [87.31, 174.61, 220.0, 261.63, 349.23], // F
      [98.0, 196.0, 246.94, 293.66, 392.0], // G
      [82.41, 164.81, 196.0, 246.94, 329.63], // Em
    ];
    const PENTA = [220.0, 261.63, 293.66, 329.63, 392.0, 440.0, 523.25, 587.33];

    const makeReverb = () => {
      const dur = 2.4;
      const rate = ac.sampleRate;
      const impulse = ac.createBuffer(2, rate * dur, rate);
      for (let ch = 0; ch < 2; ch++) {
        const data = impulse.getChannelData(ch);
        for (let i = 0; i < data.length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.6);
        }
      }
      const conv = ac.createConvolver();
      conv.buffer = impulse;
      return conv;
    };

    const playChord = (freqs) => {
      if (!running) return;
      const now = ac.currentTime;
      freqs.forEach((freq, i) => {
        const osc = ac.createOscillator();
        osc.type = i === 0 ? 'sine' : 'triangle';
        osc.frequency.value = freq;
        osc.detune.value = (Math.random() - 0.5) * 8;
        const gain = ac.createGain();
        const peak = i === 0 ? 0.05 : 0.028;
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(peak, now + 3.2);
        gain.gain.setValueAtTime(peak, now + 8.5);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 13.5);
        const filter = ac.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 760;
        osc.connect(gain).connect(filter);
        filter.connect(wet);
        filter.connect(master);
        osc.onended = () => {
          try {
            gain.disconnect();
            filter.disconnect();
            osc.disconnect();
          } catch (error) {
            /* already gone */
          }
        };
        osc.start(now);
        osc.stop(now + 14);
      });
    };

    /* one shared feedback-delay bus — per-note delay graphs would
       accumulate forever (feedback cycles are never GC'd) */
    let echoBus = null;
    const pluck = () => {
      if (!running) return;
      const now = ac.currentTime;
      const freq = PENTA[(Math.random() * PENTA.length) | 0];
      const osc = ac.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ac.createGain();
      gain.gain.setValueAtTime(0.09, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
      osc.connect(gain);
      gain.connect(wet);
      gain.connect(master);
      gain.connect(echoBus);
      osc.onended = () => {
        try {
          gain.disconnect();
          osc.disconnect();
        } catch (error) {
          /* already gone */
        }
      };
      osc.start(now);
      osc.stop(now + 1.7);
      pluckTimer = window.setTimeout(pluck, 2600 + Math.random() * 4600);
    };

    const start = () => {
      if (running) {
        if (ac && ac.state === 'suspended') ac.resume();
        return;
      }
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!ac) {
        ac = new AC();
        master = ac.createGain();
        master.gain.value = 0.5;
        master.connect(ac.destination);
        const reverb = makeReverb();
        const reverbGain = ac.createGain();
        reverbGain.gain.value = 0.55;
        wet = ac.createGain();
        wet.gain.value = 1;
        wet.connect(reverb);
        reverb.connect(reverbGain);
        reverbGain.connect(master);

        /* shared pluck echo: delay ↔ feedback gain, once for the session */
        echoBus = ac.createDelay(1.5);
        echoBus.delayTime.value = 0.42;
        const echoFeedback = ac.createGain();
        echoFeedback.gain.value = 0.34;
        echoBus.connect(echoFeedback);
        echoFeedback.connect(echoBus);
        echoFeedback.connect(wet);

        /* deep drone */
        const drone = ac.createOscillator();
        drone.type = 'sine';
        drone.frequency.value = 55;
        const droneGain = ac.createGain();
        droneGain.gain.value = 0.035;
        const lfo = ac.createOscillator();
        lfo.frequency.value = 0.06;
        const lfoGain = ac.createGain();
        lfoGain.gain.value = 0.018;
        lfo.connect(lfoGain).connect(droneGain.gain);
        drone.connect(droneGain).connect(master);
        drone.start();
        lfo.start();

        /* night air */
        const frames = ac.sampleRate * 2;
        const buf = ac.createBuffer(1, frames, ac.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
        const air = ac.createBufferSource();
        air.buffer = buf;
        air.loop = true;
        const airFilter = ac.createBiquadFilter();
        airFilter.type = 'bandpass';
        airFilter.frequency.value = 2400;
        airFilter.Q.value = 0.7;
        const airGain = ac.createGain();
        airGain.gain.value = 0.008;
        air.connect(airFilter).connect(airGain).connect(master);
        air.start();
      }
      if (ac.state === 'suspended') ac.resume();
      running = true;
      const cycle = () => {
        playChord(CHORDS[chordIndex % CHORDS.length]);
        chordIndex += 1;
        chordTimer = window.setTimeout(cycle, 11000);
      };
      cycle();
      pluckTimer = window.setTimeout(pluck, 1800);
    };

    const stop = () => {
      running = false;
      window.clearTimeout(chordTimer);
      window.clearTimeout(pluckTimer);
      if (ac && ac.state === 'running') ac.suspend();
    };

    const step = () => {
      if (!running || !ac) return;
      const now = ac.currentTime;
      const gain = ac.createGain();
      gain.gain.setValueAtTime(0.03, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
      const osc = ac.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = 70 + Math.random() * 30;
      osc.connect(gain).connect(master);
      osc.start(now);
      osc.stop(now + 0.1);
    };

    return { start, stop, step, isRunning: () => running };
  })();

  const syncAmbientBtn = () => {
    if (!ambientBtn) return;
    ambientBtn.textContent = ambientOn ? 'AMBIENT ON' : 'AMBIENT OFF';
    ambientBtn.setAttribute('aria-pressed', String(ambientOn));
  };
  syncAmbientBtn();
  ambientBtn?.addEventListener('click', () => {
    ambientOn = !ambientOn;
    try {
      localStorage.setItem(AMB_KEY, ambientOn ? '1' : '0');
    } catch (error) {
      /* fine */
    }
    if (ambientOn && mode !== 'title') music.start();
    else if (!ambientOn) music.stop();
    syncAmbientBtn();
    ambientBtn.blur(); // keep Space/E on the game, not the button
  });

  /* ================================================================
     State
     ================================================================ */
  let mode = 'title'; // title | walk | card
  let t = 0;
  let cam = 0;
  const player = {
    x: 300,
    dir: 1,
    vx: 0,
    phase: 0,
    stepAcc: 0,
  };
  const input = { left: false, right: false, run: false };
  const particles = [];
  const fireflies = Array.from({ length: 26 }, (_, i) => ({
    x: hash(i * 3.1) * WORLD,
    y: 0,
    seed: hash(i * 5.7) * 100,
  }));

  let activeExhibit = null;
  let npcTalkAt = 2500;

  const setMode = (next) => {
    mode = next;
    host.dataset.mode = next;
  };
  setMode('title');

  /* ================================================================
     Interaction cards
     ================================================================ */
  const openCard = (ex) => {
    if (!cardEl) return;
    cardKind.textContent = ex.kind;
    cardTitle.textContent = ex.title;
    cardBody.textContent = ex.body;
    cardActions.innerHTML = '';
    ex.actions.forEach((action) => {
      const a = document.createElement('a');
      a.className = 'dna-city__btn dna-city__btn--solid';
      a.href = action.href;
      a.textContent = action.label;
      if (action.external) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
      cardActions.appendChild(a);
    });
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'dna-city__btn';
    close.textContent = 'BACK TO STREET [ESC]';
    close.addEventListener('click', closeCard);
    cardActions.appendChild(close);
    setMode('card');
  };

  const closeCard = () => {
    if (mode === 'card') setMode('walk');
  };

  const startWalk = () => {
    if (mode !== 'title') return;
    setMode('walk');
    if (ambientOn) music.start();
    if (hintEl) {
      hintEl.classList.add('is-in');
      window.setTimeout(() => hintEl.classList.remove('is-in'), 6000);
    }
  };

  const interact = () => {
    if (mode === 'title') return startWalk();
    if (mode === 'card') return closeCard();
    if (activeExhibit) openCard(activeExhibit);
  };

  /* ================================================================
     Input
     ================================================================ */
  window.addEventListener('keydown', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))) {
      return;
    }
    // Focused buttons/links keep native activation; shortcuts (⌘K, /)
    // belong to the command deck; nothing drives under an open dialog;
    // Tab keeps doing focus navigation.
    if (target instanceof HTMLElement && target.closest('button, a, [role="button"]')) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (document.querySelector('dialog[open]')) return;
    const key = event.key.toLowerCase();
    if (key === '/' || key === 'tab') return;
    if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown', ' '].includes(key === ' ' ? ' ' : key)) {
      event.preventDefault();
    }
    if (mode === 'title' && key !== 'escape') return startWalk();

    if (key === 'arrowleft' || key === 'a') input.left = true;
    else if (key === 'arrowright' || key === 'd') input.right = true;
    else if (key === 'shift') input.run = true;
    else if (key === 'e' || key === 'enter' || key === 'arrowup' || key === ' ') {
      if (!event.repeat) interact();
    } else if (key === 'escape') closeCard();
  });

  window.addEventListener('keyup', (event) => {
    const key = event.key.toLowerCase();
    if (key === 'arrowleft' || key === 'a') input.left = false;
    else if (key === 'arrowright' || key === 'd') input.right = false;
    else if (key === 'shift') input.run = false;
  });

  /* touch: hold canvas halves to walk; prompt button to enter */
  let touchWalkId = null;
  canvas.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (mode === 'title') return startWalk();
    if (mode === 'card') return;
    touchWalkId = event.pointerId;
    canvas.setPointerCapture(event.pointerId);
    const half = event.clientX - canvas.getBoundingClientRect().left < W / 2;
    input.left = half;
    input.right = !half;
  });
  canvas.addEventListener('pointermove', (event) => {
    if (event.pointerId !== touchWalkId) return;
    const half = event.clientX - canvas.getBoundingClientRect().left < W / 2;
    input.left = half;
    input.right = !half;
  });
  const endTouchWalk = (event) => {
    if (event.pointerId !== touchWalkId) return;
    touchWalkId = null;
    input.left = false;
    input.right = false;
  };
  canvas.addEventListener('pointerup', endTouchWalk);
  canvas.addEventListener('pointercancel', endTouchWalk);

  promptEl?.addEventListener('click', interact);
  titleEl?.addEventListener('pointerdown', (event) => {
    if (event.target.closest('a, button')) return;
    startWalk();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) music.stop();
    else if (ambientOn && mode !== 'title') music.start();
  });

  /* ================================================================
     Update
     ================================================================ */
  const update = (dt) => {
    t += dt;

    /* player */
    if (mode === 'walk') {
      const target = (input.right ? 1 : 0) - (input.left ? 1 : 0);
      const speed = input.run ? 300 : 168;
      player.vx = lerp(player.vx, target * speed, 0.14);
      if (Math.abs(player.vx) > 4) {
        player.dir = player.vx > 0 ? 1 : -1;
        player.phase += Math.abs(player.vx) * dt * 0.055;
        player.stepAcc += Math.abs(player.vx) * dt;
        if (player.stepAcc > 46) {
          player.stepAcc = 0;
          music.step();
          particles.push({
            x: player.x - player.dir * 8,
            y: ground,
            vx: -player.dir * 16 + (Math.random() - 0.5) * 16,
            vy: -14 - Math.random() * 18,
            r: 1.6 + Math.random() * 1.8,
            life: 1,
          });
        }
      }
      player.x = clamp(player.x + player.vx * dt, 90, WORLD - 90);
    } else {
      player.vx = lerp(player.vx, 0, 0.2);
    }

    /* camera */
    const lookahead = mode === 'walk' ? player.dir * 70 : 0;
    const targetCam = clamp(player.x - W * 0.44 + lookahead, 0, Math.max(WORLD - W, 0));
    cam = reducedMotion ? targetCam : lerp(cam, targetCam, 0.055);

    /* exhibit proximity */
    activeExhibit = null;
    if (mode !== 'title') {
      for (const ex of EXHIBITS) {
        if (Math.abs(player.x - (ex.x + ex.w / 2)) < 130) {
          activeExhibit = ex;
          break;
        }
      }
    }
    if (promptEl) {
      if (activeExhibit && mode === 'walk') {
        promptEl.textContent = `⏎ ENTER — ${activeExhibit.name}`;
        promptEl.classList.add('is-in');
      } else {
        promptEl.classList.remove('is-in');
      }
    }

    /* npcs */
    const now = t * 1000;
    for (const npc of NPCS) {
      if (npc.kind === 'person' && npc.walker && mode !== 'title') {
        npc.x += npc.dir * npc.speed * dt;
        npc.phase += npc.speed * dt * 0.06;
        if (npc.x < npc.x0) npc.dir = 1;
        if (npc.x > npc.x1) npc.dir = -1;
      }
      if (npc.bubble && now > npc.bubble.until) npc.bubble = null;

      /* proximity greeting */
      if (
        npc.kind === 'person' &&
        !npc.bubble &&
        mode === 'walk' &&
        Math.abs(npc.x - player.x) < 130 &&
        now - npc.greeted > 26000
      ) {
        npc.greeted = now;
        const pool = Math.random() < 0.45 ? LINES_GREET : LINES_LORE;
        npc.bubble = { text: pool[(Math.random() * pool.length) | 0], start: now, until: now + 4600 };
      }
    }

    /* ambient chatter: one voice at a time, on-screen */
    if (now > npcTalkAt && mode !== 'title') {
      npcTalkAt = now + 3200 + Math.random() * 3800;
      const visible = NPCS.filter(
        (npc) => !npc.bubble && npc.x > cam - 60 && npc.x < cam + W + 60 && Math.abs(npc.x - player.x) > 60
      );
      if (visible.length) {
        const npc = visible[(Math.random() * visible.length) | 0];
        const pool = npc.kind === 'cat' ? LINES_CAT : Math.random() < 0.3 ? LINES_LORE : LINES_LIFE;
        npc.bubble = {
          text: pool[(Math.random() * pool.length) | 0],
          start: now,
          until: now + 4200 + Math.random() * 1200,
        };
      }
    }

    /* particles */
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life -= dt * 2.2;
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 60 * dt;
    }
  };

  /* ================================================================
     Drawing helpers
     ================================================================ */
  const wrapText = (text, maxChars) => {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    for (const word of words) {
      if ((line + ' ' + word).trim().length > maxChars) {
        if (line) lines.push(line.trim());
        line = word;
      } else {
        line += ' ' + word;
      }
    }
    if (line.trim()) lines.push(line.trim());
    return lines;
  };

  const roundRect = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  /* ================================================================
     Draw
     ================================================================ */
  const drawSky = () => {
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, W, H);

    /* stars — spread over the wrap width so every viewport is full */
    const starOff = cam * 0.02;
    for (let i = 0; i < 90; i++) {
      const sx = ((hash(i * 1.3) * (W + 40) - starOff) % (W + 40) + (W + 40)) % (W + 40) - 20;
      const sy = hash(i * 2.7) * ground * 0.62;
      const tw = reducedMotion ? 0.6 : 0.4 + Math.sin(t * (0.6 + hash(i) * 1.4) + i) * 0.3;
      ctx.fillStyle = `rgba(255,255,255,${(0.25 + hash(i * 4.1) * 0.4) * tw})`;
      ctx.fillRect(sx, sy, hash(i * 8.9) > 0.9 ? 2 : 1.2, hash(i * 8.9) > 0.9 ? 2 : 1.2);
    }

    /* moon */
    const mx = W * 0.72 - cam * 0.03;
    const my = H * 0.2;
    const mr = Math.min(84, H * 0.12);
    const glow = ctx.createRadialGradient(mx, my, mr * 0.4, mx, my, mr * 3.2);
    glow.addColorStop(0, 'rgba(255,255,255,0.18)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(mx - mr * 3.2, my - mr * 3.2, mr * 6.4, mr * 6.4);
    ctx.fillStyle = '#f4f4f4';
    ctx.beginPath();
    ctx.arc(mx, my, mr, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(10,10,10,0.1)';
    ctx.beginPath();
    ctx.arc(mx - mr * 0.3, my - mr * 0.15, mr * 0.22, 0, Math.PI * 2);
    ctx.arc(mx + mr * 0.25, my + mr * 0.3, mr * 0.15, 0, Math.PI * 2);
    ctx.arc(mx + mr * 0.1, my - mr * 0.42, mr * 0.1, 0, Math.PI * 2);
    ctx.fill();

    /* horizon fog */
    const fog = ctx.createLinearGradient(0, ground - H * 0.34, 0, ground);
    fog.addColorStop(0, 'rgba(255,255,255,0)');
    fog.addColorStop(1, 'rgba(255,255,255,0.1)');
    ctx.fillStyle = fog;
    ctx.fillRect(0, ground - H * 0.34, W, H * 0.34);
  };

  const drawFarSkyline = () => {
    const f = 0.22;
    const off = cam * f;
    const span = 170;
    const first = Math.floor(off / span) - 1;
    const last = Math.ceil((off + W) / span) + 1;
    for (let i = first; i <= last; i++) {
      const bw = 110 + hash(i * 3.3) * 90;
      const bh = 80 + hash(i * 1.9) * Math.min(190, H * 0.3);
      const bx = i * span - off;
      const by = ground - bh;
      ctx.strokeStyle = 'rgba(255,255,255,0.09)';
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, bw, bh);
      /* sparse windows */
      for (let wy = 0; wy < Math.floor(bh / 26); wy++) {
        for (let wx = 0; wx < Math.floor(bw / 24); wx++) {
          if (hash(i * 17.7 + wx * 5.3 + wy * 9.1) > 0.86) {
            ctx.fillStyle = 'rgba(255,255,255,0.28)';
            ctx.fillRect(bx + 8 + wx * 24, by + 10 + wy * 26, 7, 9);
          }
        }
      }
    }
  };

  const drawMidSkyline = () => {
    const f = 0.52;
    const off = cam * f;
    const span = 260;
    const first = Math.floor(off / span) - 1;
    const last = Math.ceil((off + W) / span) + 1;
    for (let i = first; i <= last; i++) {
      const bw = 180 + hash(i * 2.1) * 110;
      const bh = 130 + hash(i * 4.7) * Math.min(230, H * 0.36);
      const bx = i * span - off;
      const by = ground - bh;
      ctx.fillStyle = '#070707';
      ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = 'rgba(255,255,255,0.16)';
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, bw, bh);
      /* antenna / water tower */
      if (hash(i * 6.3) > 0.55) {
        ctx.beginPath();
        ctx.moveTo(bx + bw * 0.3, by);
        ctx.lineTo(bx + bw * 0.3, by - 26);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        const blink = reducedMotion ? 0.6 : (Math.sin(t * 2 + i) + 1) / 2;
        ctx.globalAlpha = 0.2 + blink * 0.5;
        ctx.fillRect(bx + bw * 0.3 - 1.5, by - 30, 3, 3);
        ctx.globalAlpha = 1;
      }
      /* windows */
      for (let wy = 0; wy < Math.floor(bh / 30); wy++) {
        for (let wx = 0; wx < Math.floor(bw / 26); wx++) {
          const lit = hash(i * 23.1 + wx * 3.7 + wy * 7.9);
          if (lit > 0.72) {
            const flick = !reducedMotion && lit > 0.965 ? (Math.sin(t * 9 + wx * wy) + 1) / 2 : 1;
            ctx.fillStyle = `rgba(255,255,255,${0.34 * flick})`;
            ctx.fillRect(bx + 9 + wx * 26, by + 12 + wy * 30, 9, 12);
          }
        }
      }
    }
  };

  const drawBillboards = () => {
    /* billboards sit just behind the street layer */
    const f = 0.82;
    const off = cam * f;
    ctx.font = '600 11px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    for (const board of BILLBOARDS) {
      const bx = board.x * f - off;
      if (bx < -260 || bx > W + 260) continue;
      const by = ground - Math.min(H * 0.42, 300);
      const bw = 230;
      const bh = 46;
      const flick = board.flicker && !reducedMotion ? (Math.sin(t * 7 + board.x) > -0.6 ? 1 : 0.25) : 1;
      ctx.globalAlpha = flick;
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(bx - bw / 2, by - bh, bw, bh);
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bx - bw / 2, by - bh, bw, bh);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      const lines = wrapText(board.text, 26);
      lines.forEach((line, li) => {
        ctx.fillText(line, bx, by - bh + 18 + li * 14);
      });
      /* legs */
      ctx.beginPath();
      ctx.moveTo(bx - bw / 4, by);
      ctx.lineTo(bx - bw / 4, by + 30);
      ctx.moveTo(bx + bw / 4, by);
      ctx.lineTo(bx + bw / 4, by + 30);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.textAlign = 'left';
  };

  const drawStreet = () => {
    /* ground plane */
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, ground + 0.5);
    ctx.lineTo(W, ground + 0.5);
    ctx.stroke();

    ctx.fillStyle = '#060606';
    ctx.fillRect(0, ground + 1, W, H - ground);

    /* lane dashes */
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 2;
    const dashOff = cam % 90;
    for (let x = -dashOff; x < W; x += 90) {
      ctx.beginPath();
      ctx.moveTo(x, ground + (H - ground) * 0.55);
      ctx.lineTo(x + 34, ground + (H - ground) * 0.55);
      ctx.stroke();
    }

    /* exhibits (street buildings) */
    ctx.font = '600 12px "JetBrains Mono", monospace';
    for (const ex of EXHIBITS) {
      const bx = ex.x - cam;
      if (bx + ex.w < -120 || bx > W + 120) continue;
      const bh = Math.min(ex.h, ground * 0.6);
      const by = ground - bh;

      /* building shell */
      ctx.fillStyle = '#0d0d0d';
      ctx.fillRect(bx, by, ex.w, bh);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bx, by, ex.w, bh);

      /* signal tower extra mast */
      if (ex.name === 'SIGNAL TOWER') {
        const cxm = bx + ex.w / 2;
        ctx.beginPath();
        ctx.moveTo(cxm, by);
        ctx.lineTo(cxm, by - 70);
        ctx.stroke();
        if (!reducedMotion) {
          const pulse = ((t * 0.7) % 1);
          ctx.strokeStyle = `rgba(255,255,255,${0.5 * (1 - pulse)})`;
          ctx.beginPath();
          ctx.arc(cxm, by - 70, 6 + pulse * 26, -Math.PI * 0.85, -Math.PI * 0.15);
          ctx.stroke();
        }
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillRect(cxm - 2, by - 72, 4, 4);
      }

      /* door with light spill */
      const doorW = 34;
      const doorH = 52;
      const dx = bx + ex.w / 2 - doorW / 2;
      const dy = ground - doorH;
      const isNear = activeExhibit === ex;
      const spill = ctx.createRadialGradient(dx + doorW / 2, ground, 6, dx + doorW / 2, ground, 90);
      spill.addColorStop(0, `rgba(255,255,255,${isNear ? 0.22 : 0.12})`);
      spill.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = spill;
      ctx.fillRect(dx - 90, ground - 10, doorW + 180, 60);
      ctx.fillStyle = `rgba(255,255,255,${isNear ? 0.85 : 0.55})`;
      ctx.fillRect(dx, dy, doorW, doorH);
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(dx + 3, dy + 3, doorW - 6, doorH - 3);
      ctx.fillStyle = `rgba(255,255,255,${isNear ? 0.8 : 0.4})`;
      ctx.fillRect(dx + doorW - 9, dy + doorH / 2, 3, 6);

      /* windows */
      for (let wx = 0; wx < Math.floor(ex.w / 56); wx++) {
        const wxp = bx + 18 + wx * 56;
        if (Math.abs(wxp + 14 - (dx + doorW / 2)) < 44) continue;
        ctx.fillStyle = `rgba(255,255,255,${hash(ex.x + wx) > 0.4 ? 0.3 : 0.1})`;
        ctx.fillRect(wxp, by + bh * 0.32, 28, 22);
      }

      /* hanging sign */
      const signW = Math.min(ctx.measureText(ex.sign).width + 26, ex.w - 12);
      const sx = bx + ex.w / 2 - signW / 2;
      const sy = by + 14;
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(sx, sy, signW, 26);
      ctx.strokeStyle = `rgba(255,255,255,${isNear ? 0.95 : 0.6})`;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(sx, sy, signW, 26);
      ctx.fillStyle = `rgba(255,255,255,${isNear ? 0.95 : 0.7})`;
      ctx.textAlign = 'center';
      ctx.fillText(ex.sign, bx + ex.w / 2, sy + 17);
      ctx.textAlign = 'left';

      /* marker diamond */
      const bob = reducedMotion ? 0 : Math.sin(t * 3 + ex.x) * 4;
      const mxp = bx + ex.w / 2;
      const myp = by - 18 + bob;
      ctx.save();
      ctx.translate(mxp, myp);
      ctx.rotate(Math.PI / 4);
      ctx.strokeStyle = `rgba(255,255,255,${isNear ? 1 : 0.55})`;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(-5, -5, 10, 10);
      if (isNear) {
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillRect(-2.5, -2.5, 5, 5);
      }
      ctx.restore();
    }

    /* lampposts + wires */
    const lampSpan = 430;
    const firstLamp = Math.floor(cam / lampSpan) - 1;
    const lastLamp = Math.ceil((cam + W) / lampSpan) + 1;
    for (let i = firstLamp; i <= lastLamp; i++) {
      const lx = i * lampSpan + 190 - cam;
      if (lx < -60 || lx > W + 60) continue;
      const lh = 92;
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(lx, ground);
      ctx.lineTo(lx, ground - lh);
      ctx.lineTo(lx + 16, ground - lh);
      ctx.stroke();
      /* head + light pool */
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fillRect(lx + 12, ground - lh - 3, 10, 6);
      const pool = ctx.createRadialGradient(lx + 17, ground, 4, lx + 17, ground, 70);
      pool.addColorStop(0, 'rgba(255,255,255,0.13)');
      pool.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = pool;
      ctx.fillRect(lx - 60, ground - 40, 160, 60);
      /* wires to next lamp */
      const nx = (i + 1) * lampSpan + 190 - cam;
      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(lx, ground - lh);
      ctx.quadraticCurveTo((lx + nx) / 2, ground - lh + 26, nx, ground - lh);
      ctx.stroke();

      /* bench every other lamp */
      if (i % 2 === 0) {
        const bx = lx + 70;
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 2;
        ctx.strokeRect(bx, ground - 16, 44, 4);
        ctx.beginPath();
        ctx.moveTo(bx + 5, ground - 12);
        ctx.lineTo(bx + 5, ground);
        ctx.moveTo(bx + 39, ground - 12);
        ctx.lineTo(bx + 39, ground);
        ctx.stroke();
      }
    }
  };

  const drawFireflies = () => {
    for (const fly of fireflies) {
      const fx = fly.x - cam + (reducedMotion ? 0 : Math.sin(t * 0.7 + fly.seed) * 26);
      if (fx < -20 || fx > W + 20) continue;
      const fy = ground - 30 - hash(fly.seed) * 90 + (reducedMotion ? 0 : Math.sin(t * 1.1 + fly.seed * 2) * 16);
      const pulse = reducedMotion ? 0.5 : (Math.sin(t * (1.4 + hash(fly.seed)) + fly.seed) + 1) / 2;
      ctx.fillStyle = `rgba(255,255,255,${0.14 + pulse * 0.5})`;
      ctx.beginPath();
      ctx.arc(fx, fy, 1.4 + pulse, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawPerson = (npc) => {
    const x = npc.x - cam;
    if (x < -60 || x > W + 60) return;
    const hgt = npc.h;
    const wid = npc.wBody;
    const walking = npc.walker && mode !== 'title';
    const bob = walking ? Math.abs(Math.sin(npc.phase)) * 2 : Math.sin(t * 1.4 + npc.phase) * 1.2;
    const y = ground - bob;
    const fill = npc.white ? '#e8e8e8' : '#0a0a0a';
    const rim = npc.white ? 'rgba(10,10,10,0.9)' : 'rgba(255,255,255,0.55)';

    ctx.save();
    ctx.translate(x, y);
    ctx.scale(npc.dir, 1);

    /* legs */
    ctx.strokeStyle = fill === '#0a0a0a' ? '#0a0a0a' : '#e8e8e8';
    ctx.lineWidth = 3.4;
    const swing = walking ? Math.sin(npc.phase) * 7 : 1.6;
    ctx.beginPath();
    ctx.moveTo(-3, -hgt * 0.42);
    ctx.lineTo(-3 + swing, 0);
    ctx.moveTo(3, -hgt * 0.42);
    ctx.lineTo(3 - swing, 0);
    ctx.stroke();
    /* leg rim */
    ctx.strokeStyle = rim;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(-3, -hgt * 0.42);
    ctx.lineTo(-3 + swing, 0);
    ctx.stroke();

    /* body: coat or skirt silhouette */
    ctx.fillStyle = fill;
    ctx.strokeStyle = rim;
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (npc.skirt) {
      ctx.moveTo(-wid * 0.35, -hgt * 0.86);
      ctx.lineTo(wid * 0.35, -hgt * 0.86);
      ctx.lineTo(wid * 0.62, -hgt * 0.36);
      ctx.lineTo(-wid * 0.62, -hgt * 0.36);
    } else {
      ctx.moveTo(-wid * 0.42, -hgt * 0.88);
      ctx.lineTo(wid * 0.42, -hgt * 0.88);
      ctx.lineTo(wid * 0.52, -hgt * 0.3);
      ctx.lineTo(-wid * 0.52, -hgt * 0.3);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    /* head */
    ctx.beginPath();
    ctx.arc(0, -hgt * 0.94, wid * 0.36, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    /* hat */
    if (npc.hat === 1) {
      ctx.fillRect(-wid * 0.55, -hgt * 1.02, wid * 1.1, 2.4);
      ctx.fillRect(-wid * 0.3, -hgt * 1.12, wid * 0.6, hgt * 0.1);
    } else if (npc.hat === 2) {
      ctx.beginPath();
      ctx.arc(0, -hgt * 0.98, wid * 0.34, Math.PI, 0);
      ctx.fill();
    }

    /* umbrella */
    if (npc.umbrella) {
      ctx.strokeStyle = fill === '#0a0a0a' ? 'rgba(255,255,255,0.7)' : 'rgba(10,10,10,0.8)';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(wid * 0.5, -hgt * 0.6);
      ctx.lineTo(wid * 0.72, -hgt * 1.18);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(wid * 0.72, -hgt * 1.18, wid * 0.95, Math.PI, 0);
      ctx.stroke();
    }

    ctx.restore();

    drawBubble(npc, x, y - hgt - (npc.umbrella ? 26 : 12));
  };

  const drawCat = (npc) => {
    const x = npc.x - cam;
    if (x < -40 || x > W + 40) return;
    const y = ground;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(npc.dir, 1);
    ctx.fillStyle = '#0a0a0a';
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 0.8;
    /* body */
    ctx.beginPath();
    ctx.ellipse(0, -7, 11, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    /* head + ears */
    ctx.beginPath();
    ctx.arc(9, -14, 5.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(6, -18);
    ctx.lineTo(7.5, -23);
    ctx.lineTo(10, -18.5);
    ctx.moveTo(11, -18.5);
    ctx.lineTo(13, -22.5);
    ctx.lineTo(14, -17.5);
    ctx.fill();
    /* tail */
    ctx.strokeStyle = '#0a0a0a';
    ctx.lineWidth = 3;
    const wag = reducedMotion ? 0 : Math.sin(t * 2 + npc.phase) * 6;
    ctx.beginPath();
    ctx.moveTo(-10, -8);
    ctx.quadraticCurveTo(-18, -14 + wag, -16, -22 + wag);
    ctx.stroke();
    /* eye */
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(10.4, -15.4, 1.6, 1.6);
    ctx.restore();

    drawBubble(npc, x, y - 30);
  };

  const drawBubble = (npc, x, topY) => {
    if (!npc.bubble) return;
    const now = t * 1000;
    const typed = reducedMotion
      ? npc.bubble.text
      : npc.bubble.text.slice(0, Math.floor((now - npc.bubble.start) / 26));
    if (!typed) return;
    ctx.font = '10.5px "JetBrains Mono", monospace';
    const lines = wrapText(typed, 26);
    const widest = lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
    const pad = 8;
    const lh = 14;
    const bw = widest + pad * 2;
    const bh = lines.length * lh + pad * 2 - 4;
    let bx = clamp(x - bw / 2, 8, W - bw - 8);
    const by = topY - bh - 12;

    ctx.fillStyle = 'rgba(244,244,244,0.96)';
    roundRect(bx, by, bw, bh, 7);
    ctx.fill();
    /* tail */
    ctx.beginPath();
    ctx.moveTo(clamp(x, bx + 10, bx + bw - 10) - 4, by + bh);
    ctx.lineTo(clamp(x, bx + 10, bx + bw - 10) + 4, by + bh);
    ctx.lineTo(clamp(x, bx + 10, bx + bw - 10), by + bh + 7);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#0a0a0a';
    lines.forEach((line, i) => {
      ctx.fillText(line, bx + pad, by + pad + 8 + i * lh);
    });
  };

  const drawPlayer = () => {
    const x = player.x - cam;
    const moving = Math.abs(player.vx) > 4;
    const runFactor = clamp(Math.abs(player.vx) / 300, 0, 1);
    const bob = moving ? Math.abs(Math.sin(player.phase)) * 2.6 : Math.sin(t * 1.6) * 1.4;
    const y = ground - bob;
    const hgt = 62;

    ctx.save();
    ctx.translate(x, y);
    ctx.scale(player.dir, 1);
    ctx.rotate(moving ? runFactor * 0.06 : 0);

    const rim = 'rgba(255,255,255,0.75)';
    const ink = '#050505';

    /* legs — long confident stride */
    const swing = moving ? Math.sin(player.phase) * (8 + runFactor * 5) : 2;
    const swingB = moving ? Math.sin(player.phase + Math.PI) * (8 + runFactor * 5) : -2;
    ctx.strokeStyle = ink;
    ctx.lineWidth = 4.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-3, -hgt * 0.4);
    ctx.lineTo(-3 + swing, 0);
    ctx.moveTo(4, -hgt * 0.4);
    ctx.lineTo(4 + swingB, 0);
    ctx.stroke();
    ctx.strokeStyle = rim;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-3, -hgt * 0.4);
    ctx.lineTo(-3 + swing, 0);
    ctx.stroke();

    /* long coat — flares behind with speed */
    const flare = 6 + runFactor * 12 + (moving ? Math.sin(player.phase * 0.5) * 2 : 0);
    ctx.fillStyle = ink;
    ctx.strokeStyle = rim;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(-7, -hgt * 0.92);
    ctx.lineTo(8, -hgt * 0.92);
    ctx.lineTo(10, -hgt * 0.62);
    ctx.lineTo(9, -hgt * 0.3);
    ctx.lineTo(-9 - flare, -hgt * 0.24);
    ctx.lineTo(-8, -hgt * 0.6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    /* scarf tail — flutters */
    const flutter = reducedMotion ? 0 : Math.sin(t * 6 + player.phase) * 4;
    ctx.beginPath();
    ctx.moveTo(-4, -hgt * 0.84);
    ctx.quadraticCurveTo(-14 - runFactor * 10, -hgt * 0.8 + flutter, -20 - runFactor * 16, -hgt * 0.68 + flutter * 1.6);
    ctx.lineWidth = 4;
    ctx.strokeStyle = ink;
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.strokeStyle = rim;
    ctx.stroke();

    /* head + hood */
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.arc(2, -hgt * 0.99, 6.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = rim;
    ctx.lineWidth = 1.2;
    ctx.stroke();
    /* hood peak */
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.moveTo(-5, -hgt * 0.99);
    ctx.quadraticCurveTo(-2, -hgt * 1.16, 6, -hgt * 1.08);
    ctx.quadraticCurveTo(2, -hgt * 1.02, -5, -hgt * 0.96);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    /* dust */
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    for (const p of particles) {
      ctx.globalAlpha = clamp(p.life, 0, 1) * 0.5;
      ctx.beginPath();
      ctx.arc(p.x - cam, p.y, p.r * p.life, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  const drawVignette = () => {
    const vg = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.42, W / 2, H / 2, Math.max(W, H) * 0.72);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
  };

  const draw = () => {
    drawSky();
    drawFarSkyline();
    drawMidSkyline();
    drawBillboards();
    drawStreet();
    drawFireflies();
    for (const npc of NPCS) {
      if (npc.kind === 'cat') drawCat(npc);
      else drawPerson(npc);
    }
    drawPlayer();
    drawVignette();
  };

  /* ================================================================
     Main loop
     ================================================================ */
  let last = performance.now();
  const frame = (now) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    if (!document.hidden) {
      update(dt);
      draw();
    }
    window.requestAnimationFrame(frame);
  };
  window.requestAnimationFrame(frame);
})();
